import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Modal, Button, Field, Input, Select, Spinner } from "./ui";
import { WORK_STATUSES, PRIORITY_LEVELS, levelFromValue, statusLabel } from "./work";

const valueFromLevel = (lvl) => PRIORITY_LEVELS.find((p) => p.key === lvl)?.value ?? null;

export default function GoalFormModal({ open, onClose, onSaved, entityId, spaceId, editing, parentGoalId, keyResults = [] }) {
  const { t, lang } = useApp();
  const isEdit = !!editing;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? {
      name: editing.name || "", description: editing.description || "",
      status: editing.status || "pending", champion: editing.champion || "",
      start_date: editing.start_date || "", due_date: editing.due_date || "",
      impact_lvl: levelFromValue(editing.impact), effort_lvl: levelFromValue(editing.effort),
      key_result_id: editing.key_result_id || "",
    } : {
      name: "", description: "", status: "pending", champion: "",
      start_date: "", due_date: "", impact_lvl: "high", effort_lvl: "medium",
      key_result_id: "",
    });
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name?.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), description: form.description, status: form.status,
        champion: form.champion, start_date: form.start_date || null, due_date: form.due_date || null,
        impact: valueFromLevel(form.impact_lvl), effort: valueFromLevel(form.effort_lvl),
        key_result_id: form.key_result_id || null,
      };
      let data;
      if (isEdit) {
        ({ data } = await api.put(`/goals/${editing.id}`, payload));
      } else {
        ({ data } = await api.post("/goals", {
          ...payload, entity_id: entityId, space_id: spaceId,
          parent_goal_id: parentGoalId || null, targets: [], checklist: [],
        }));
      }
      onSaved(data);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t("edit_goal") : (parentGoalId ? t("add_subgoal") : t("new_goal"))}
      footer={<>
        <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
        <Button onClick={save} disabled={saving || !form.name?.trim()} data-testid="goal-save">
          {saving && <Spinner className="w-3.5 h-3.5" />}{isEdit ? t("save") : t("create")}
        </Button>
      </>}>
      <Field label={t("goal_name")}>
        <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="goal-name"
          placeholder={lang === "fr" ? "ex : Augmenter le MRR" : "e.g. Grow MRR"} autoFocus />
      </Field>
      <Field label={t("description") + " " + t("optional")}>
        <Input value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="goal-description" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("impact")}>
          <Select value={form.impact_lvl || ""} onChange={(e) => set("impact_lvl", e.target.value)} data-testid="goal-impact">
            {PRIORITY_LEVELS.map((p) => <option key={p.key} value={p.key}>{t(`prio_${p.key}`)}</option>)}
          </Select>
        </Field>
        <Field label={t("effort")}>
          <Select value={form.effort_lvl || ""} onChange={(e) => set("effort_lvl", e.target.value)} data-testid="goal-effort">
            {PRIORITY_LEVELS.map((p) => <option key={p.key} value={p.key}>{t(`prio_${p.key}`)}</option>)}
          </Select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("start_date")}>
          <Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="goal-start" />
        </Field>
        <Field label={t("due_date")}>
          <Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} data-testid="goal-due" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("status")}>
          <Select value={form.status || "pending"} onChange={(e) => set("status", e.target.value)} data-testid="goal-status">
            {WORK_STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s, lang)}</option>)}
          </Select>
        </Field>
        <Field label={t("champion") + " " + t("optional")}>
          <Input value={form.champion || ""} onChange={(e) => set("champion", e.target.value)} data-testid="goal-champion" />
        </Field>
      </div>
      {keyResults.length > 0 && (
        <Field label={t("linked_key_result") + " " + t("optional")}>
          <Select value={form.key_result_id || ""} onChange={(e) => set("key_result_id", e.target.value)} data-testid="goal-key-result">
            <option value="">{t("none")}</option>
            {keyResults.map((k) => (
              <option key={k.id} value={k.id}>{k.objective_title ? `${k.objective_title} › ${k.title}` : k.title}</option>
            ))}
          </Select>
        </Field>
      )}
    </Modal>
  );
}
